import React, { useState } from "react";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrButton } from "./CrButton";
import { CrDropdown } from "./CrDropdown";
import { CrIconButton } from "./CrIconButton";
import { CrInput } from "./CrInput";
import dayjs, { Dayjs } from "dayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface CrCalendarDateLabel {
  year?: string;
  month?: string;
}

export interface CrCalendarProps {
  selectedDate?: { year?: number; month?: number; day?: number } | Date;
  minDate: Date | number | "TODAY";
  maxDate: Date | number | "TODAY";
  closable: boolean;
  typeable?: boolean;
  format: "YYYY/MM/DD" | "YYYY-MM-DD" | "DD-MMM-YYYY" | "DD/MMM/YYYY";
  dateLabel?: CrCalendarDateLabel;
  errorMessage?: {
    invalidFormat?: string;
  };
  timezone?: string;

  onDismiss?: () => void;
  onSelectDate?: (date: Date) => void;
  onYearChange?: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
}

type CalAttr = "TODAY" | "HOLIDAY" | "SUN" | "SAT" | "DISABLED" | "SELECTED" | "GHOST";
type CAL = { year: number; month: number; day: number; attr?: Array<CalAttr> };

export function CrCalendar(props: CrCalendarProps) {
  const {
    selectedDate: selectedDateX,
    minDate,
    maxDate,
    errorMessage,
    onSelectDate,
    onYearChange,
    onMonthChange,
    onDismiss,
    closable,
    format,
    typeable,
    dateLabel,
    timezone,
  } = props;
  const selectedDate: Date | null = (() => {
    if (selectedDateX instanceof Date) return selectedDateX;
    else if (selectedDateX !== null && typeof selectedDateX === "object") {
      const c = dayjs();

      return dayjs
        .tz(
          `${selectedDateX.year || c.year()}-${String(selectedDateX.month || c.month() + 1).padStart(2, "0")}-${String(
            selectedDateX.day || c.date()
          ).padStart(2, "0")}`,
          timezone
        )
        .toDate();
    } else {
      return null;
    }
  })();

  const [dateOnTyped, setDateOnTyped] = useState<string>("");

  const currentDate = dayjs();

  const minVal = Math.min(
    selectedDate ? selectedDate.getTime() : 9 * 10 ** 13,
    minDate instanceof Date
      ? minDate.getTime()
      : typeof minDate === "number"
      ? currentDate.subtract(minDate, "days").valueOf()
      : currentDate.valueOf()
  );

  const maxVal = Math.max(
    selectedDate ? selectedDate.getTime() : 1,
    maxDate instanceof Date
      ? maxDate.getTime()
      : typeof maxDate === "number"
      ? currentDate.add(maxDate, "days").valueOf()
      : currentDate.valueOf()
  );

  const years: number[] = (() => {
    const min = dayjs(minVal).year();
    const max = dayjs(maxVal).year();

    return "*"
      .repeat(max - min + 1)
      .split("")
      .map((_, i) => min + i)
      .reverse();
  })();

  const months: number[] = "*"
    .repeat(12)
    .split("")
    .map((_, i) => i + 1);

  const [year, setYear] = useState(
    selectedDate ? dayjs(selectedDate).tz(timezone).year() : dayjs(Math.min(maxVal, currentDate.valueOf())).tz(timezone).year()
  );
  const [month, setMonth] = useState(selectedDate ? dayjs(selectedDate).tz(timezone).month() + 1 : currentDate.tz(timezone).month() + 1);

  const dates: Array<Array<CAL>> = (() => {
    const rows: Array<Array<CAL>> = [];
    let cols: Array<CAL> | null = null;
    const firstDay = dayjs(`${year}-${String(month).padStart(2, "0")}-01`);
    const lastDay = firstDay.add(1, "month").subtract(1, "day");
    const lastDate = lastDay.date();
    const attr = (d: Dayjs): Array<CalAttr> => {
      const as: Array<CalAttr> = [];

      if (selectedDate && d.format("YYYY-MM-DD") === dayjs(selectedDate).tz(timezone).format("YYYY-MM-DD")) as.push("SELECTED");
      if (d.format("YYYY-MM-DD") === currentDate.tz(timezone).format("YYYY-MM-DD")) as.push("TODAY");
      if (d.format("YYYY-MM-DD") > dayjs(maxVal).tz(timezone).format("YYYY-MM-DD")) as.push("DISABLED");
      if (d.format("YYYY-MM-DD") < dayjs(minVal).tz(timezone).format("YYYY-MM-DD")) as.push("DISABLED");
      if (d.day() === 0) as.push("SUN");
      if (d.day() === 6) as.push("SAT");
      if (d.month() !== firstDay.month()) as.push("GHOST");

      return as;
    };

    for (let i = 1; i <= lastDate; i++) {
      if (!cols) cols = [];

      if (i === 1) {
        const fdd = firstDay.day();

        for (let k = 0; k < fdd; k++) {
          const bd = firstDay.subtract(fdd - k, "days");

          cols.push({ year: bd.year(), month: bd.month() + 1, day: bd.date(), attr: attr(bd) });
        }
      }

      const cd = firstDay.add(i - 1, "days");

      cols.push({ year: cd.year(), month: cd.month() + 1, day: cd.date(), attr: attr(cd) });

      if (i === lastDate) {
        const ldd = 6 - lastDay.day();

        for (let k = 0; k < ldd; k++) {
          const bd = lastDay.add(k + 1, "days");

          cols.push({ year: bd.year(), month: bd.month() + 1, day: bd.date(), attr: attr(bd) });
        }
      }

      if (cols.length === 7) {
        rows.push(cols);
        cols = null;
      }
    }

    if (cols && cols.length > 0) {
      rows.push(cols);
    }

    return rows;
  })();

  const isValidateDateOnTyped = (): boolean => {
    const formattedDate = dayjs(dateOnTyped, format).format(format);

    if (formattedDate !== dateOnTyped) {
      return false;
    } else if (dayjs(formattedDate).isAfter(maxVal)) {
      return false;
    } else if (dayjs(formattedDate).isBefore(minVal)) {
      return false;
    }

    return true;
  };

  return (
    <>
      <div className={classNames("cr-calendar", closable && "--closable")}>
        <div className="cr-calendar-header">
          <div className="before">
            <CrIconButton
              icon="ChevronLeft"
              disabled={`${year}-${String(month).padStart(2, "0")}-01` <= dayjs(minVal).format("YYYY-MM-01")}
              onClick={() => {
                const prev = dayjs(`${year}-${String(month).padStart(2, "0")}-01`).subtract(1, "month");

                setYear(prev.year());
                setMonth(prev.month() + 1);

                onYearChange && onYearChange(prev.toDate());
                onMonthChange && onMonthChange(prev.toDate());
              }}
            />
          </div>
          <div className="cr-calendar-navi">
            <div className="year">
              <CrDropdown
                calloutClassName="cr-calendar-navi--year"
                defaultKey={String(year)}
                items={years.map((year) => ({ key: String(year), text: String(year) }))}
                onValueChange={(val) => {
                  setYear(parseInt(val as string, 10));
                  onYearChange && onYearChange(dayjs(`${val}-${String(month).padStart(2, "0")}-01`).toDate());
                }}
              />
              {dateLabel?.year || "년"}
            </div>
            <div className="month">
              <CrDropdown
                defaultKey={String(month)}
                items={months.map((month) => ({ key: String(month), text: String(month) }))}
                onValueChange={(val) => {
                  setMonth(parseInt(val as string, 10));
                  onMonthChange && onMonthChange(dayjs(`${year}-${String(val).padStart(2, "0")}-01`).toDate());
                }}
              />{" "}
              {dateLabel?.month || "월"}
            </div>
          </div>
          <div className="next">
            <CrIconButton
              disabled={`${year}-${String(month).padStart(2, "0")}-01` >= dayjs(maxVal).format("YYYY-MM-01")}
              onClick={() => {
                const next = dayjs(`${year}-${String(month).padStart(2, "0")}-01`).add(1, "month");

                setYear(next.year());
                setMonth(next.month() + 1);

                onYearChange && onYearChange(next.toDate());
                onMonthChange && onMonthChange(next.toDate());
              }}
              icon="ChevronRight"
            />
          </div>
          {closable && (
            <div className="close">
              <CrIconButton color="primary" onClick={() => onDismiss && onDismiss()} icon="CalculatorMultiply" />
            </div>
          )}
        </div>
        {typeable && (
          <div className="cr-calendar-typeable">
            <CrInput
              value={dateOnTyped}
              className="--input"
              maxLength={format.length}
              onValueChange={(val) => {
                if (dateOnTyped.length > val.length) {
                  setDateOnTyped(val);
                  return;
                }

                if (format === "YYYY/MM/DD") {
                  val = val.replace(/[^\d]/g, "");

                  val = val.length >= 4 ? val.substring(0, 4) + "/" + val.substring(4, val.length) : val;
                  val = val.length >= 7 ? val.substring(0, 7) + "/" + val.substring(7, val.length) : val;
                } else if (format === "YYYY-MM-DD") {
                  val = val.replace(/[^\d]/g, "");

                  val = val.length >= 4 ? val.substring(0, 4) + "-" + val.substring(4, val.length) : val;
                  val = val.length >= 7 ? val.substring(0, 7) + "-" + val.substring(7, val.length) : val;
                } else if (format === "DD-MMM-YYYY") {
                  val = val.replace(/[^\da-zA-Z]/g, "");

                  val =
                    val.length >= 2
                      ? val.substring(0, 2) + "-" + val.substring(2, 3).toUpperCase() + val.substring(3, val.length).toLowerCase()
                      : val;
                  val = val.length >= 6 ? val.substring(0, 6) + "-" + val.substring(6, val.length) : val;
                } else if (format === "DD/MMM/YYYY") {
                  val = val.replace(/[^\da-zA-Z]/g, "");

                  val =
                    val.length >= 2
                      ? val.substring(0, 2) + "/" + val.substring(2, 3).toUpperCase() + val.substring(3, val.length).toLowerCase()
                      : val;
                  val = val.length >= 6 ? val.substring(0, 6) + "/" + val.substring(6, val.length) : val;
                }

                setDateOnTyped(val);
              }}
              onEnterKeyDown={() => {
                if (!isValidateDateOnTyped()) {
                  return;
                }

                if (onSelectDate) {
                  onSelectDate(dayjs(dateOnTyped, format).toDate());
                }
                onDismiss && onDismiss();
              }}
              errorMessage={
                dateOnTyped && !isValidateDateOnTyped() ? errorMessage?.invalidFormat || "입력된 날짜가 올바르지 않습니다." : undefined
              }
              placeholder={format}
            />
            <CrButton
              disabled={!isValidateDateOnTyped()}
              onClick={() => {
                if (onSelectDate) {
                  onSelectDate(dayjs(dateOnTyped, format).toDate());
                }
                onDismiss && onDismiss();
              }}
            >
              Enter
            </CrButton>
          </div>
        )}
        <div className="cr-calendar-dates">
          <table>
            <thead>
              <tr>
                <th className="--sun">SUN</th>
                <th>MON</th>
                <th>TUE</th>
                <th>WED</th>
                <th>THU</th>
                <th>FRI</th>
                <th className="--sat">SAT</th>
              </tr>
            </thead>
            <tbody>
              {dates.map((weeks, wi) => (
                <tr key={wi}>
                  {weeks.map((date, di) => (
                    <td key={`${wi}-${di}`} className={classNames(date.attr?.map((e) => `--${e.toLowerCase()}`).join(" "))}>
                      <button
                        tabIndex={-1}
                        disabled={date.attr?.indexOf("DISABLED") !== -1}
                        onClick={() => {
                          onSelectDate &&
                            onSelectDate(
                              dayjs
                                .tz(`${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`, timezone)
                                .toDate()
                            );
                        }}
                      >
                        {date.day}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

CrCalendar.defaultProps = {
  minDate: dayjs().subtract(90, "years").toDate(),
  maxDate: dayjs().add(2, "years").toDate(),
  closable: true,
};
